import { StudyProgramQuota } from "@bindeleddet/core/types";

/**
 * Interface for quota comparison that only requires the fields needed for duplicate checking
 */
interface QuotaComparison {
  study_programme: number;
  year_levels_accepted: number[];
  id?: number;
}

/**
 * Pre-computes a normalized string representation of year levels for efficient comparison.
 * This avoids repeated sorting and stringification operations.
 */
const getNormalizedYearLevelsKey = (yearLevels: number[]): string => {
  return JSON.stringify([...yearLevels].sort((a, b) => a - b));
};

/**
 * Checks if a quota has a duplicate combination of study_programme
 * and year_levels_accepted within a list of quotas.
 *
 * @param quota - The quota to check for duplicates (can be a partial quota for comparison)
 * @param allQuotas - The list of all quotas to check against
 * @param excludeId - Optional ID to exclude from the duplicate check (e.g., the quota being updated)
 * @returns The duplicate quota if found, undefined otherwise
 */
export const findDuplicateQuotaCombination = (
  quota: QuotaComparison,
  allQuotas: (QuotaComparison | StudyProgramQuota)[],
  excludeId?: number
): QuotaComparison | StudyProgramQuota | undefined => {
  const quotaKey = getNormalizedYearLevelsKey(quota.year_levels_accepted);

  return allQuotas.find(otherQuota => {
    if (excludeId !== undefined && otherQuota.id === excludeId) {
      return false;
    }
    if (otherQuota.study_programme !== quota.study_programme) {
      return false;
    }
    const otherKey = getNormalizedYearLevelsKey(otherQuota.year_levels_accepted);
    return otherKey === quotaKey;
  });
};

/**
 * Checks if there are any duplicate combinations of study_programme and year_levels_accepted
 * within a list of quotas. Uses pre-computed normalized keys for efficient comparison.
 *
 * @param quotas - The list of quotas to check for duplicates
 * @returns The first duplicate quota found, undefined if no duplicates exist
 */
export const hasDuplicateQuotaCombinations = (
  quotas: (QuotaComparison | StudyProgramQuota)[]
): QuotaComparison | StudyProgramQuota | undefined => {
  // Pre-compute normalized keys for all quotas to avoid repeated sorting/stringification
  const quotaKeys = new Map<number, string>();
  quotas.forEach(quota => {
    if (quota.id !== undefined) {
      quotaKeys.set(quota.id, getNormalizedYearLevelsKey(quota.year_levels_accepted));
    }
  });

  return quotas.find((quota, index) => {
    const quotaKey =
      quota.id !== undefined ? quotaKeys.get(quota.id) : getNormalizedYearLevelsKey(quota.year_levels_accepted);
    return quotas.some((otherQuota, otherIndex) => {
      if (index === otherIndex) return false;
      if (otherQuota.study_programme !== quota.study_programme) return false;
      const otherKey =
        otherQuota.id !== undefined
          ? quotaKeys.get(otherQuota.id)
          : getNormalizedYearLevelsKey(otherQuota.year_levels_accepted);
      return otherKey === quotaKey;
    });
  });
};
